import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  CircularProgress,
} from '@mui/material';
import { FormAutocomplete } from '@/components/common/FormComponents';
import { CollaboratorDto } from '@/api/ApiClient';
import { useAuthStore } from '@/stores/authStore';
import { useBackend } from '@/hooks/useBackend';
import { useSnackbar } from '@/hooks/useSnackbar';
import { log } from '@/lib/utils/logger';

interface SignInAsDialogProps {
  open: boolean;
  users?: CollaboratorDto[]; // Pre-loaded users/staff list
  onClose: () => void;
}

type SignInAsDialogContentProps = {
  preloadedUsers?: CollaboratorDto[];
  onClose: () => void;
};

function SignInAsDialogContent({ preloadedUsers, onClose }: SignInAsDialogContentProps) {
  const [users, setUsers] = useState<CollaboratorDto[]>(() => preloadedUsers ?? []);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { impersonateUser } = useAuthStore();
  const { showSnackbar } = useSnackbar();
  const backend = useBackend();

  useEffect(() => {
    if (preloadedUsers) {
      return;
    }

    let cancelled = false;

    const fetchUsersAndStaff = async () => {
      setIsLoading(true);
      try {
        const [fetchedUsers, staff] = await Promise.all([backend.user.getUsers(false), backend.user.getStaff(false)]);

        const sortedUsers = [
          ...new Map([...fetchedUsers, ...staff].map((user) => [user.fullName + user.roleIds?.[0], user])).values(),
        ].sort((a, b) => (a.fullName ?? '').localeCompare(b.fullName ?? ''));

        if (!cancelled) {
          setUsers(sortedUsers);
        }
      } catch (error) {
        log.error('[SignInAsDialog] Failed to fetch users and staff:', error);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void fetchUsersAndStaff();

    return () => {
      cancelled = true;
    };
  }, [backend, preloadedUsers]);

  const handleSignInAs = async () => {
    if (!selectedUser) return;
    try {
      await impersonateUser(selectedUser);
      const userName = users.find((u) => u.id === selectedUser)?.fullName ?? selectedUser;
      log.info(`[SignInAsDialog] Impersonating user ${selectedUser}`);
      showSnackbar(`Signed in as ${userName}`, 'success');
      onClose();
    } catch (error) {
      log.error('[SignInAsDialog] Failed to impersonate user:', error);
      showSnackbar('Failed to sign in as user', 'error');
    }
  };

  const resetForm = () => {
    setSelectedUser(null);
  };

  const generateUserLabel = (user: CollaboratorDto) => {
    return `${user.fullName} (${user.roles?.[0]?.name})`;
  };

  return (
    <>
      <DialogTitle id="sign-in-as-modal-title">Sign In As</DialogTitle>

      <DialogContent>
        {isLoading ? (
          <CircularProgress />
        ) : (
          <FormControl fullWidth sx={{ mt: 2 }}>
            <FormAutocomplete
              options={users}
              getOptionLabel={generateUserLabel}
              label="Select User"
              value={users.find((user) => user.id === selectedUser) || null}
              onChange={(_, newValue: CollaboratorDto | null) => setSelectedUser(newValue?.id ?? null)}
            />
          </FormControl>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={() => {
            resetForm();
            onClose();
          }}
          color="secondary"
          variant="outlined"
        >
          Cancel
        </Button>
        <Button onClick={() => void handleSignInAs()} color="primary" variant="contained" disabled={!selectedUser}>
          Continue
        </Button>
      </DialogActions>
    </>
  );
}

export function SignInAsDialog({ open, users: preloadedUsers, onClose }: SignInAsDialogProps) {
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="sign-in-as-modal-title"
      aria-describedby="sign-in-as-modal-description"
      maxWidth="sm"
      fullWidth
    >
      {open ? (
        <SignInAsDialogContent
          key={preloadedUsers ? 'preloaded' : 'fetch'}
          preloadedUsers={preloadedUsers}
          onClose={handleClose}
        />
      ) : null}
    </Dialog>
  );
}
